// GroupPropertiesSection — namn + färg + member-count + save/delete för
// en vald group. Verbatim extraherad ur PropertiesPanel.tsx (#8,
// POST_QA_SUBPLAN Wave E.1 step 2).
//
// State-ägaren förblir PropertiesPanel; localGroupName-state passas in
// kontrollerat. Inga side-effects ändras.

import { Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/i18n/LanguageContext';
import type { BlockGroup } from '@/types/workflow';

interface GroupPropertiesSectionProps {
  selectedGroup: BlockGroup;
  localGroupName: string;
  onLocalGroupNameChange: (next: string) => void;
  onUpdateGroup: (id: string, patch: Partial<BlockGroup>) => void;
  onSave: () => void;
  onDelete: () => void;
}

export function GroupPropertiesSection({
  selectedGroup, localGroupName, onLocalGroupNameChange,
  onUpdateGroup, onSave, onDelete,
}: GroupPropertiesSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="group-name">{t('name')}</Label>
        <Input
          id="group-name"
          value={localGroupName}
          onChange={(e) => onLocalGroupNameChange(e.target.value)}
          placeholder="T.ex. Onboarding steg"
        />
      </div>

      <div className="space-y-2">
        <Label>{t('groupColor')}</Label>
        <div className="flex gap-2">
          <input
            type="color"
            value={selectedGroup.color}
            onChange={(e) => onUpdateGroup(selectedGroup.id, { color: e.target.value })}
            className="w-10 h-10 rounded-lg border border-border cursor-pointer"
            aria-label={t('groupColor')}
            data-testid="group-color-picker"
          />
          <Input
            value={selectedGroup.color}
            onChange={(e) => onUpdateGroup(selectedGroup.id, { color: e.target.value })}
            className="flex-1"
            aria-label={`${t('groupColor')} hex value`}
            data-testid="group-color-hex"
          />
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        {selectedGroup.blockIds.length} {t('blocksInGroup')}
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={onSave} className="flex-1" data-testid="save-group">
          <Save className="w-4 h-4 mr-2" aria-hidden="true" />
          {t('save')}
        </Button>
        <Button variant="destructive" onClick={onDelete} aria-label={t('deleteGroup') || 'Delete group'} data-testid="delete-group">
          <Trash2 className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
